import { Button } from "@/components/ui/Button";
import { cn } from "@/utils/cn";
import { Check, ChevronDown, ChevronUp, GripVertical, Loader2, Lock, Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ReviewedColumn, ReviewedSources } from "../api";
import type { FastColumn } from "../types";
import { TYPES, parseOptions } from "./ColumnPanel";
import { type SourceChoice, SourceReview } from "./SourceReview";
import { FadeInput, FadeTextarea } from "./Fade";

type Item = {
  id: number;
  key: string | null;
  label: string;
  description: string;
  value_type: FastColumn["value_type"];
  options: string;
  required: boolean;
  fixed: boolean;
  /** The proposed question and how it was shown: an unedited description sends the question back as it was. */
  question: string | null;
  shown: string;
};

/** Edits to a dataset's proposed columns and sources not yet approved, kept while the user looks elsewhere. */
const drafts = new Map<string, { items: Item[]; nextId: number; sources: SourceChoice | null }>();

const field =
  "w-full rounded-[3px] border border-transparent bg-transparent px-2 py-1 text-zinc-800 placeholder:text-zinc-400 hover:border-zinc-200 focus:border-zinc-300 focus:bg-white focus:outline-none disabled:hover:border-transparent";

const problem = (item: Item) =>
  !item.label.trim()
    ? "Needs a name"
    : item.value_type === "category" && parseOptions(item.options).length < 2
      ? "A category needs at least two options"
      : null;

/**
 * The proposed columns, before any document is read: rename, redescribe, retype, reorder (drag or
 * the arrows), remove or add columns, mark columns required, then build. The columns each document's
 * rows are split on (one row per drug and dose) keep their question and stay; they can be renamed,
 * moved and required.
 */
export const ColumnReview = ({
  columns,
  fixedKeys,
  describe,
  busy,
  draftKey,
  sources: planned,
  onBuild,
}: {
  columns: FastColumn[];
  fixedKeys: Set<string>;
  describe: (column: FastColumn) => string;
  busy: boolean;
  /** The dataset the edits are kept for. */
  draftKey: string;
  /** The planner's sources; the user's choice is sent only if they changed it. */
  sources: SourceChoice;
  onBuild: (columns: ReviewedColumn[], sources: ReviewedSources | null) => void;
}) => {
  const saved = drafts.get(draftKey);
  const [sources, setSources] = useState<SourceChoice | null>(saved?.sources ?? null);
  const nextId = useRef(saved?.nextId ?? columns.length);
  const [items, setItems] = useState<Item[]>(() =>
    saved?.items ?? columns.map((c, i) => ({
      id: i,
      key: c.key,
      label: c.label,
      description: describe(c),
      value_type: c.value_type,
      options: (c.options ?? []).join(", "),
      required: !!c.required,
      fixed: fixedKeys.has(c.key),
      question: c.question,
      shown: describe(c),
    })),
  );
  useEffect(() => {
    drafts.set(draftKey, { items, nextId: nextId.current, sources });
  }, [draftKey, items, sources]);
  const [grab, setGrab] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);

  const update = (id: number, patch: Partial<Item>) =>
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  const move = (from: number, to: number) =>
    setItems((current) => {
      if (to < 0 || to >= current.length || from === to) return current;
      const next = [...current];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  const add = () => {
    const id = nextId.current++;
    setItems((current) => [
      ...current,
      { id, key: null, label: "", description: "", value_type: "text", options: "", required: false, fixed: false, question: null, shown: "" },
    ]);
    window.requestAnimationFrame(() => document.getElementById(`review-label-${id}`)?.focus());
  };

  const firstProblem = items.map(problem).find(Boolean) ?? null;
  const build = () => {
    if (busy || firstProblem || items.length === 0) return;
    drafts.delete(draftKey);
    onBuild(
      items.map((item) => ({
        key: item.key,
        label: item.label.trim(),
        description: item.question !== null && item.description.trim() === item.shown ? item.question : item.description.trim(),
        value_type: item.value_type,
        options: item.value_type === "category" ? parseOptions(item.options) : [],
        required: item.required,
      })),
      sources && { sources: sources.sources, row_source: sources.rowSource, papers: sources.papers },
    );
  };

  const buildButton = (
    <Button size="sm" onClick={build} disabled={busy || !!firstProblem || items.length === 0}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Check className="h-4 w-4" aria-hidden />}
      Approve
    </Button>
  );

  return (
    <section className="mt-4 animate-in fade-in" aria-label="Review sources and columns">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-zinc-300 pb-3">
        <div>
          <h2 className="text-[15px] font-medium text-zinc-900">Approve or modify the proposed sources and columns</h2>
          <p className="mt-0.5 text-xs text-zinc-500">
            Pick the sources to search. Rename, describe, retype, drag to reorder, or remove columns. A row shows only if it has a value in every required column. Nothing is read until you approve.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {firstProblem && <span className="font-ui text-2xs text-red-600">{firstProblem}</span>}
          {buildButton}
        </div>
      </div>
      <SourceReview value={sources ?? planned} onChange={setSources} />
      <div className="font-ui grid grid-cols-[20px_16px_minmax(10rem,14rem)_1fr_7.5rem_4.5rem_4.5rem] gap-x-2 border-b border-zinc-100 py-1.5 text-3xs uppercase tracking-wider text-zinc-400">
        <span />
        <span />
        <span className="px-2">Name</span>
        <span className="px-2">What to find in each source</span>
        <span>Type</span>
        <span>Required</span>
        <span />
      </div>
      <ol>
        {items.map((item, index) => {
          const issue = problem(item);
          return (
            <li
              key={item.id}
              draggable={grab === item.id}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                setDragging(item.id);
              }}
              onDragOver={(e) => {
                if (dragging === null) return;
                e.preventDefault();
                const from = items.findIndex((i) => i.id === dragging);
                if (from !== index) move(from, index);
              }}
              onDragEnd={() => {
                setDragging(null);
                setGrab(null);
              }}
              className={cn(
                "group grid grid-cols-[20px_16px_minmax(10rem,14rem)_1fr_7.5rem_4.5rem_4.5rem] items-start gap-x-2 border-b border-zinc-100 py-1.5 transition-colors",
                dragging === item.id && "bg-zinc-50 opacity-60",
              )}
            >
              <button
                type="button"
                aria-label={`Drag ${item.label || "column"}`}
                onMouseDown={() => setGrab(item.id)}
                onMouseUp={() => setGrab(null)}
                className="mt-1 cursor-grab text-zinc-300 hover:text-zinc-600 active:cursor-grabbing"
              >
                <GripVertical className="h-4 w-4" aria-hidden />
              </button>
              <span className="font-ui mt-1.5 text-right text-2xs tabular-nums text-zinc-400">{index + 1}</span>
              <div className="min-w-0">
                <FadeInput
                  id={`review-label-${item.id}`}
                  value={item.label}
                  onChange={(e) => update(item.id, { label: e.target.value })}
                  maxLength={80}
                  placeholder="Column name"
                  aria-label="Column name"
                  className={cn(field, "text-sm font-medium")}
                />
                {item.fixed && (
                  <p className="font-ui flex items-center gap-1 px-2 text-3xs text-zinc-400">
                    <Lock className="h-2.5 w-2.5" aria-hidden /> Splits rows by arm · rename only
                  </p>
                )}
              </div>
              <div className="min-w-0">
                <FadeTextarea
                  value={item.description}
                  onChange={(e) => update(item.id, { description: e.target.value })}
                  disabled={item.fixed}
                  maxLength={600}
                  rows={Math.min(3, Math.max(1, Math.ceil(item.description.length / 150)))}
                  placeholder="What to find in each source"
                  aria-label="Column description"
                  className={cn(field, "resize-none text-[13px] leading-5 text-zinc-600")}
                />
                {item.value_type === "category" && (
                  <FadeInput
                    value={item.options}
                    onChange={(e) => update(item.id, { options: e.target.value })}
                    placeholder="Options, separated by commas"
                    aria-label="Category options"
                    className={cn(field, "text-[13px] text-zinc-600")}
                  />
                )}
                {issue && <p className="font-ui px-2 text-3xs text-red-600">{issue}</p>}
              </div>
              <select
                value={item.value_type}
                onChange={(e) => update(item.id, { value_type: e.target.value as Item["value_type"] })}
                disabled={item.fixed}
                aria-label="Column type"
                className="font-ui mt-0.5 w-full rounded-[3px] border border-zinc-200 bg-white py-0.5 pl-2 pr-7 text-xs text-zinc-600 focus:ring-1 focus:ring-zinc-400 disabled:border-transparent disabled:bg-transparent disabled:opacity-70"
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <label className="mt-1 flex justify-center" title="Leave out rows without a value in this column">
                <input
                  type="checkbox"
                  checked={item.required}
                  onChange={(e) => update(item.id, { required: e.target.checked })}
                  aria-label={`${item.label || "Column"} required`}
                  className="h-3.5 w-3.5 rounded-[2px] border-zinc-300 text-zinc-900 focus:ring-zinc-400"
                />
              </label>
              <div className="flex items-center gap-0.5 pt-1 text-zinc-300 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                <button type="button" aria-label="Move up" disabled={index === 0} onClick={() => move(index, index - 1)} className="p-0.5 hover:text-zinc-700 disabled:opacity-30">
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button type="button" aria-label="Move down" disabled={index === items.length - 1} onClick={() => move(index, index + 1)} className="p-0.5 hover:text-zinc-700 disabled:opacity-30">
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${item.label || "column"}`}
                  disabled={item.fixed}
                  onClick={() => setItems((current) => current.filter((i) => i.id !== item.id))}
                  className="p-0.5 hover:text-red-600 disabled:invisible"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          );
        })}
      </ol>
      <button type="button" onClick={add} className="font-ui mt-3 flex items-center gap-1 text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-900">
        <Plus className="h-3.5 w-3.5" aria-hidden /> Add column
      </button>
    </section>
  );
};
